import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import GooglePlacesAutocomplete, { geocodeByPlaceId } from "react-google-places-autocomplete";
import { useUser } from "../../context/UserContext";
import { calculateSenatorsAndRep, calculateSenatorsAndRepByDistrict, updateFullProfile, getAuthHeader, saveUserAddressFlags, getMemberPhotoUrl, checkUsernameAvailability, setUsername as setUsernameApi } from "../../utils/utils";
import { getDistrictLookupErrorMessage, isDistrictLookupUnavailable } from "../../utils/districtLookupErrors";
import { STATE_DISTRICT_MAP } from "../../utils/discoverApi";
import { suggestAvailableUsername, USERNAME_VALIDATION_REGEX } from "../../utils/usernameSuggestion";
import {
  logLegacyUsernamePromptEvent,
  setLegacyUsernamePromptDismissed,
} from "../../utils/legacyUsernamePrompt";
import stateOptions from "../../components/stateOptions";
import axios from "axios";
import { isPoliticianAccount } from "../../utils/accountTypes";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const GOOGLE_PLACES_API_KEY =
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_PUBLIC_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_APP_GOOGLE_CIVIC_API_KEY ||
  "";

const ENTRY_MODE = {
  SEARCH: "search",
  MANUAL: "manual",
  DISTRICT: "district",
};

/** District choices for a state. Single-seat states get one at-large option. */
function getDistrictChoices(stateCode) {
  const seats = STATE_DISTRICT_MAP[stateCode];
  if (!seats) return [];
  if (seats === 1) return [{ value: "0", label: "At-Large (one district)" }];
  return Array.from({ length: seats }, (_, i) => ({
    value: String(i + 1),
    label: `District ${i + 1}`,
  }));
}

export default function SetupRepresentative() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    setUserRepsName,
    setUserRepsBioguideID,
    setUserSenOneName,
    setUserSenOneBioguideID,
    setUserSenTwoName,
    setUserSenTwoBioguideID,
    loadUserContext,
    refreshFirebaseUser,
    userFirstName,
    userLastName,
    entityName: contextEntityName,
    municipalityName: contextMunicipalityName,
    municipalityDepartment: contextMunicipalityDepartment,
    userRepsName: contextRepsName,
    userRepsBioguideID: contextRepsBioguideID,
    userSenOneName: contextSenOneName,
    userSenOneBioguideID: contextSenOneBioguideID,
    userSenTwoName: contextSenTwoName,
    userSenTwoBioguideID: contextSenTwoBioguideID,
    setAddressSkipped,
    accountTypeName: contextAccountTypeName,
    hasUsername,
    publicOfficeStatus: contextOfficeStatus,
    publicOfficeLevel: contextOfficeLevel,
    publicOfficePosition: contextOfficePosition,
  } = useUser();

  const accountTypeName = location.state?.accountTypeName || contextAccountTypeName || "citizen";
  const publicOfficeStatus =
    location.state?.public_office_status ?? contextOfficeStatus;
  const publicOfficePosition =
    location.state?.public_office_position ?? contextOfficePosition;
  const publicOfficeLevel =
    location.state?.public_office_level ?? contextOfficeLevel;
  const changeMode = location.state?.changeMode || false;

  const isOrganizationAccount = () => ["organization", "non_profit"].includes(accountTypeName);
  const isMunicipalityAccount = () => accountTypeName === "municipality";
  const isCurrentRepresentative = () =>
    publicOfficeStatus === "incumbent" && publicOfficePosition === "us_representative";
  const isCurrentSenator = () =>
    publicOfficeStatus === "incumbent" && publicOfficePosition === "us_senator";
  const isDCResident = () => formData.state?.toUpperCase() === "DC";

  const [confirmMode, setConfirmMode] = useState(false);
  const [hasExitedConfirmMode, setHasExitedConfirmMode] = useState(false);
  const [showLocationOnProfile, setShowLocationOnProfile] = useState(true);
  /** When true, we're in changeMode and still fetching address — show loading so we don't flash "Let's set you up" before "Confirm your address". */
  const [changeModeAddressChecked, setChangeModeAddressChecked] = useState(!(location.state?.changeMode));

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    entity_name: "",
    street_1: "",
    city: "",
    state: "",
    zipcode: "",
    district_number: "",
    hasMetRep: undefined,
    hasMetSenator1: undefined,
    hasMetSenator2: undefined,
    municipality_name: "",
    municipality_department: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [repName, setRepName] = useState(null);
  const [senatorOneName, setSenatorOneName] = useState(null);
  const [senatorTwoName, setSenatorTwoName] = useState(null);
  const [repCalculated, setRepCalculated] = useState(false);

  const [repBioguideId, setRepBioguideId] = useState(null);
  const [senatorOneBioguideId, setSenatorOneBioguideId] = useState(null);
  const [senatorTwoBioguideId, setSenatorTwoBioguideId] = useState(null);
  const [repPhotoUrl, setRepPhotoUrl] = useState(null);
  const [senatorOnePhotoUrl, setSenatorOnePhotoUrl] = useState(null);
  const [senatorTwoPhotoUrl, setSenatorTwoPhotoUrl] = useState(null);

  const [isThisYouAnswer, setIsThisYouAnswer] = useState(null);
  const [selectedSenatorBioguideId, setSelectedSenatorBioguideId] = useState(null);
  const [showSenatorPicker, setShowSenatorPicker] = useState(false);

  const [showStatePicker, setShowStatePicker] = useState(false);

  const showOnboardingUsername = !changeMode && !hasUsername;
  const [onboardingUsername, setOnboardingUsername] = useState("");
  const [usernameValidationState, setUsernameValidationState] = useState("idle");
  const [usernameValidationMessage, setUsernameValidationMessage] = useState("");
  const [usernameSuggestionLoaded, setUsernameSuggestionLoaded] = useState(false);
  const currentUsernameRef = useRef("");
  const validatedUsernameRef = useRef("");
  const usernameManuallyEditedRef = useRef(false);
  const debouncedOnboardingUsername = useDebounce(onboardingUsername, 500);
  const onboardingUsernameReady =
    !showOnboardingUsername ||
    (usernameValidationState === "available" &&
      validatedUsernameRef.current === onboardingUsername);

  const hasApiKey = !!GOOGLE_PLACES_API_KEY;
  const [entryMode, setEntryMode] = useState(
    GOOGLE_PLACES_API_KEY ? ENTRY_MODE.SEARCH : ENTRY_MODE.MANUAL
  );
  const [selectedPlace, setSelectedPlace] = useState(null);

  const isDistrictMode = entryMode === ENTRY_MODE.DISTRICT;
  const isSearchMode = entryMode === ENTRY_MODE.SEARCH && hasApiKey;
  const isManualMode = !isDistrictMode && !isSearchMode;

  const districtChoices = getDistrictChoices(formData.state);
  const onlyDistrictValue = districtChoices.length === 1 ? districtChoices[0].value : null;

  // At-large states have one seat, so there's nothing for the user to choose.
  useEffect(() => {
    if (!isDistrictMode || !onlyDistrictValue) return;
    setFormData((prev) =>
      prev.district_number === onlyDistrictValue
        ? prev
        : { ...prev, district_number: onlyDistrictValue }
    );
  }, [isDistrictMode, onlyDistrictValue]);

  const parseAddressComponents = useCallback((details) => {
    if (!details?.address_components) return null;
    const components = details.address_components;
    let streetNumber = "";
    let route = "";
    let city = "";
    let state = "";
    let zipcode = "";
    components.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) streetNumber = component.long_name;
      else if (types.includes("route")) route = component.long_name;
      else if (types.includes("locality") || types.includes("sublocality") || types.includes("neighborhood")) {
        if (!city) city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        state = component.short_name;
      } else if (types.includes("postal_code")) {
        zipcode = component.long_name;
      }
    });
    return {
      street_1: streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber,
      city,
      state,
      zipcode,
    };
  }, []);

  const handlePlaceSelection = useCallback(
    async (placeOption) => {
      setSelectedPlace(placeOption);
      const placeId = placeOption?.value?.place_id;
      if (!placeId) return;

      try {
        const geocodeResults = await geocodeByPlaceId(placeId);
        const primaryResult = geocodeResults?.[0];
        const parsed = parseAddressComponents(primaryResult);
        if (parsed) {
          setFormData((prev) => ({
            ...prev,
            street_1: parsed.street_1 || prev.street_1,
            city: parsed.city || prev.city,
            state: parsed.state || prev.state,
            zipcode: parsed.zipcode || prev.zipcode,
          }));
        }
      } catch (err) {
        setError("Unable to load address details. Please try another address.");
      }
    },
    [parseAddressComponents]
  );

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      first_name: prev.first_name || userFirstName || "",
      last_name: prev.last_name || userLastName || "",
      entity_name: prev.entity_name || contextEntityName || "",
      municipality_name: prev.municipality_name || contextMunicipalityName || "",
      municipality_department:
        prev.municipality_department || contextMunicipalityDepartment || "",
    }));
  }, [
    userFirstName,
    userLastName,
    contextEntityName,
    contextMunicipalityName,
    contextMunicipalityDepartment,
  ]);

  useEffect(() => {
    if (!showOnboardingUsername || usernameSuggestionLoaded) return;
    let cancelled = false;

    (async () => {
      const suggestion = await suggestAvailableUsername({
        firstName: formData.first_name || userFirstName,
        lastName: formData.last_name || userLastName,
        email: user?.email,
        displayName: user?.displayName,
      });
      if (cancelled) return;
      if (suggestion && !usernameManuallyEditedRef.current) {
        currentUsernameRef.current = suggestion;
        validatedUsernameRef.current = suggestion;
        setOnboardingUsername(suggestion);
        setUsernameValidationState("available");
        setUsernameValidationMessage("This username is available!");
      }
      setUsernameSuggestionLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showOnboardingUsername,
    usernameSuggestionLoaded,
    formData.first_name,
    formData.last_name,
    userFirstName,
    userLastName,
    user?.email,
    user?.displayName,
  ]);

  useEffect(() => {
    if (!showOnboardingUsername) return;

    // Ignore a debounced value that no longer matches what is displayed.
    if (currentUsernameRef.current !== debouncedOnboardingUsername) return;

    if (!debouncedOnboardingUsername) {
      validatedUsernameRef.current = "";
      setUsernameValidationState("idle");
      setUsernameValidationMessage("");
      return;
    }

    if (debouncedOnboardingUsername.length < 3) {
      setUsernameValidationState("invalid");
      setUsernameValidationMessage("Username must be at least 3 characters.");
      return;
    }

    if (!USERNAME_VALIDATION_REGEX.test(debouncedOnboardingUsername)) {
      setUsernameValidationState("invalid");
      setUsernameValidationMessage(
        "Only lowercase letters, numbers, and underscores are allowed."
      );
      return;
    }

    // The suggestion helper already checked this exact value. Do not issue a
    // second request that can overwrite a valid suggestion with a transient
    // network/authentication failure.
    if (validatedUsernameRef.current === debouncedOnboardingUsername) {
      setUsernameValidationState("available");
      setUsernameValidationMessage("This username is available!");
      return;
    }

    let cancelled = false;

    async function check() {
      setUsernameValidationState("checking");
      setUsernameValidationMessage("Checking availability…");

      try {
        const result = await checkUsernameAvailability(debouncedOnboardingUsername);
        if (
          cancelled ||
          currentUsernameRef.current !== debouncedOnboardingUsername
        ) {
          return;
        }

        if (result.available) {
          validatedUsernameRef.current = debouncedOnboardingUsername;
          setUsernameValidationState("available");
          setUsernameValidationMessage("This username is available!");
        } else {
          validatedUsernameRef.current = "";
          setUsernameValidationState("taken");
          setUsernameValidationMessage(
            result.error || "This username is already taken."
          );
        }
      } catch {
        if (
          !cancelled &&
          currentUsernameRef.current === debouncedOnboardingUsername
        ) {
          validatedUsernameRef.current = "";
          setUsernameValidationState("invalid");
          setUsernameValidationMessage("Could not check availability. Try again.");
        }
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [debouncedOnboardingUsername, showOnboardingUsername]);

  const saveOnboardingUsername = useCallback(async ({ reloadContext = true } = {}) => {
    if (!showOnboardingUsername) return;
    if (
      usernameValidationState !== "available" ||
      validatedUsernameRef.current !== onboardingUsername
    ) {
      throw new Error("Please choose a valid username before continuing.");
    }
    await setUsernameApi(onboardingUsername);
    setLegacyUsernamePromptDismissed(false);
    logLegacyUsernamePromptEvent("username_saved", {
      surface: "web_setup_representative",
      changeMode: false,
    });
    if (reloadContext) {
      await loadUserContext();
    }
  }, [
    showOnboardingUsername,
    usernameValidationState,
    onboardingUsername,
    loadUserContext,
  ]);

  // In changeMode, fetch existing address to pre-fill and go straight to confirm view when address exists
  useEffect(() => {
    if (!changeMode) return;
    let cancelled = false;

    const loadAddress = async () => {
      try {
        const headers = await getAuthHeader();
        const [addrRes, profileRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/users/me/address/`, { headers }),
          axios.get(`${API_BASE_URL}/api/users/me/full_profile/`, { headers }),
        ]);
        const addr = addrRes.data || {};
        const usr = profileRes.data?.user || {};
        if (cancelled) return;

        const hasAddress = !!(addr.street_1 && addr.city && addr.state && addr.zipcode);
        setFormData((prev) => ({
          ...prev,
          street_1: prev.street_1 || addr.street_1 || "",
          city: prev.city || addr.city || "",
          state: prev.state || addr.state || "",
          zipcode: prev.zipcode || addr.zipcode || "",
          hasMetRep: usr.has_met_representative === true ? true : usr.has_met_representative === false ? false : undefined,
          hasMetSenator1: usr.has_met_senator_one === true ? true : usr.has_met_senator_one === false ? false : undefined,
          hasMetSenator2: usr.has_met_senator_two === true ? true : usr.has_met_senator_two === false ? false : undefined,
        }));
        if (hasAddress) {
          setRepName(contextRepsName || null);
          setRepBioguideId(contextRepsBioguideID || null);
          setSenatorOneName(contextSenOneName || null);
          setSenatorOneBioguideId(contextSenOneBioguideID || null);
          setSenatorTwoName(contextSenTwoName || null);
          setSenatorTwoBioguideId(contextSenTwoBioguideID || null);
          setConfirmMode(true);
          setRepCalculated(true);
        }
        setChangeModeAddressChecked(true);
      } catch (err) {
        if (!cancelled) setChangeModeAddressChecked(true);
      }
    };

    loadAddress();
    return () => { cancelled = true; };
  }, [changeMode, contextRepsName, contextRepsBioguideID, contextSenOneName, contextSenOneBioguideID, contextSenTwoName, contextSenTwoBioguideID]);

  // Enter confirm mode when in changeMode with existing address (only if we didn't already do it in loadAddress)
  useEffect(() => {
    if (!changeMode || hasExitedConfirmMode || confirmMode || repCalculated || !changeModeAddressChecked) return;

    const hasAddress = formData.street_1 && formData.city && formData.state && formData.zipcode;
    if (!hasAddress) return;

    setConfirmMode(true);
    if (contextRepsName) {
      setRepName(contextRepsName);
      setRepBioguideId(contextRepsBioguideID);
    }
    if (contextSenOneName) {
      setSenatorOneName(contextSenOneName);
      setSenatorOneBioguideId(contextSenOneBioguideID);
    }
    if (contextSenTwoName) {
      setSenatorTwoName(contextSenTwoName);
      setSenatorTwoBioguideId(contextSenTwoBioguideID);
    }
    setRepCalculated(true);
  }, [
    changeMode, hasExitedConfirmMode, confirmMode, repCalculated, changeModeAddressChecked,
    formData.street_1, formData.city, formData.state, formData.zipcode,
    contextRepsName, contextRepsBioguideID,
    contextSenOneName, contextSenOneBioguideID,
    contextSenTwoName, contextSenTwoBioguideID,
  ]);

  // Resolve congress member profile photos (same source as member cards)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (repBioguideId) {
        try {
          const url = await getMemberPhotoUrl(repBioguideId);
          if (!cancelled && url) setRepPhotoUrl(url);
          else if (!cancelled) setRepPhotoUrl(null);
        } catch {
          if (!cancelled) setRepPhotoUrl(null);
        }
      } else {
        setRepPhotoUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [repBioguideId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (senatorOneBioguideId) {
        try {
          const url = await getMemberPhotoUrl(senatorOneBioguideId);
          if (!cancelled && url) setSenatorOnePhotoUrl(url);
          else if (!cancelled) setSenatorOnePhotoUrl(null);
        } catch {
          if (!cancelled) setSenatorOnePhotoUrl(null);
        }
      } else {
        setSenatorOnePhotoUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [senatorOneBioguideId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (senatorTwoBioguideId) {
        try {
          const url = await getMemberPhotoUrl(senatorTwoBioguideId);
          if (!cancelled && url) setSenatorTwoPhotoUrl(url);
          else if (!cancelled) setSenatorTwoPhotoUrl(null);
        } catch {
          if (!cancelled) setSenatorTwoPhotoUrl(null);
        }
      } else {
        setSenatorTwoPhotoUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [senatorTwoBioguideId]);

  const getAllQuestionsAnswered = () => {
    if (isOrganizationAccount() || isMunicipalityAccount()) return true;

    const repAnswered = isCurrentRepresentative()
      ? formData.hasMetRep !== undefined
      : formData.hasMetRep !== null && formData.hasMetRep !== undefined;

    if (isDCResident()) return repAnswered;

    const senator1Answered = isCurrentSenator()
      ? formData.hasMetSenator1 !== undefined
      : formData.hasMetSenator1 !== null && formData.hasMetSenator1 !== undefined;

    const senator2Answered = isCurrentSenator()
      ? formData.hasMetSenator2 !== undefined
      : formData.hasMetSenator2 !== null && formData.hasMetSenator2 !== undefined;

    return repAnswered && senator1Answered && senator2Answered;
  };

  const allQuestionsAnswered = getAllQuestionsAnswered();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError(null);

    if (!user) {
      setError("You must be logged in to calculate your representatives. Please log in and try again.");
      return;
    }

    let emailVerified = user.emailVerified;
    if (!emailVerified) {
      const refreshed = await refreshFirebaseUser();
      emailVerified = Boolean(refreshed?.emailVerified);
    }
    if (!emailVerified) {
      setError("Please verify your email address before setting up your representatives. Check your inbox for a verification link and click it, then return here.");
      return;
    }

    if (isDistrictMode) {
      if (!formData.state) {
        setError("Please select your state.");
        return;
      }
      if (!formData.district_number) {
        setError("Please select your congressional district.");
        return;
      }
    } else {
      const { street_1, city, state, zipcode } = formData;
      if (!street_1.trim() || !city.trim() || !state || !zipcode.trim()) {
        setError(
          isManualMode
            ? "Please complete all address fields before determining your representatives."
            : "We couldn't calculate your representatives from that place. Please choose a full street address (with street number and ZIP code), or use Enter Manually."
        );
        return;
      }
    }

    setLoading(true);

    try {
      const { representative, senator_one, senator_two } = isDistrictMode
        ? await calculateSenatorsAndRepByDistrict(formData)
        : await calculateSenatorsAndRep(formData);

      setRepName(representative?.name || "Unknown Representative");
      setRepBioguideId(representative?.bioguideId || null);
      setSenatorOneBioguideId(senator_one?.bioguideId || null);
      setSenatorTwoBioguideId(senator_two?.bioguideId || null);

      if (isDCResident()) {
        setSenatorOneName("N/A (DC has no senators)");
        setSenatorTwoName("N/A (DC has no senators)");
      } else {
        setSenatorOneName(senator_one?.name || "Unknown");
        setSenatorTwoName(senator_two?.name || "Unknown");
      }

      if (representative?.name && representative.name !== "Unknown Representative") {
        setUserRepsName(representative.name);
        setUserRepsBioguideID(representative.bioguideId || null);
      }
      if (senator_one?.name && senator_one.name !== "Unknown") {
        setUserSenOneName(senator_one.name);
        setUserSenOneBioguideID(senator_one.bioguideId || null);
      }
      if (senator_two?.name && senator_two.name !== "Unknown") {
        setUserSenTwoName(senator_two.name);
        setUserSenTwoBioguideID(senator_two.bioguideId || null);
      }

      await loadUserContext();
      setRepCalculated(true);
      setIsThisYouAnswer(null);
      setSelectedSenatorBioguideId(null);
      setShowSenatorPicker(false);
    } catch (err) {
      // Census is down — move the user onto the address-free path instead of
      // leaving them stuck on a lookup that can't succeed right now.
      const shouldFallBackToDistrict = !isDistrictMode && isDistrictLookupUnavailable(err);
      if (shouldFallBackToDistrict) {
        setEntryMode(ENTRY_MODE.DISTRICT);
      }

      const districtLookupMessage = getDistrictLookupErrorMessage(err, {
        allowSkip: !shouldFallBackToDistrict,
        allowDistrictFallback: shouldFallBackToDistrict,
      });
      let errorMessage = districtLookupMessage;
      if (!errorMessage) {
        if (err.code === "ECONNABORTED") {
          errorMessage = "Request timed out. Please check your internet connection and try again.";
        } else if (!err.response) {
          errorMessage = "Network error. Please check your internet connection and try again.";
        } else if (err.response?.status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (err.response?.status === 403) {
          const detail = err.response?.data?.detail || "";
          if (detail.toLowerCase().includes("verified") || detail.toLowerCase().includes("email")) {
            errorMessage = "Please verify your email address and try again.";
          } else {
            errorMessage = "Access denied. Please ensure your email is verified and you are logged in, then try again.";
          }
        } else if (err.response?.status === 400) {
          errorMessage = err.response?.data?.error || err.response?.data?.detail || "Invalid address. Please check your information and try again.";
        } else if (err.response?.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = "Failed to calculate representative.";
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const buildUsernameNavigationState = useCallback(
    (returnToRepsView, claimedBioguideId = null) => {
      const base = {
        fromSetupRep: true,
        returnToRepsView,
        changeMode,
        accountTypeName,
        public_office_status: publicOfficeStatus,
        public_office_level: publicOfficeLevel,
        public_office_position: publicOfficePosition,
        claimed_bioguide_id: claimedBioguideId,
      };
      if (!returnToRepsView) {
        return base;
      }
      return {
        ...base,
        setupRestore: {
          repName,
          senatorOneName,
          senatorTwoName,
          repBioguideId,
          senatorOneBioguideId,
          senatorTwoBioguideId,
          isThisYouAnswer,
          selectedSenatorBioguideId,
          hasMetRep: formData.hasMetRep,
          hasMetSenator1: formData.hasMetSenator1,
          hasMetSenator2: formData.hasMetSenator2,
          confirmMode,
          repCalculated: true,
          street_1: formData.street_1,
          city: formData.city,
          state: formData.state,
          zipcode: formData.zipcode,
          entity_name: formData.entity_name,
          municipality_name: formData.municipality_name,
          municipality_department: formData.municipality_department,
          first_name: formData.first_name,
          last_name: formData.last_name,
        },
      };
    },
    [
      changeMode,
      accountTypeName,
      publicOfficeStatus,
      publicOfficeLevel,
      publicOfficePosition,
      repName,
      senatorOneName,
      senatorTwoName,
      repBioguideId,
      senatorOneBioguideId,
      senatorTwoBioguideId,
      isThisYouAnswer,
      selectedSenatorBioguideId,
      formData.hasMetRep,
      formData.hasMetSenator1,
      formData.hasMetSenator2,
      formData.street_1,
      formData.city,
      formData.state,
      formData.zipcode,
      formData.entity_name,
      formData.municipality_name,
      formData.municipality_department,
      formData.first_name,
      formData.last_name,
      confirmMode,
    ]
  );

  const hasCompleteAddress = useCallback(() => {
    const { street_1, city, state, zipcode } = formData;
    return Boolean(
      street_1?.trim() && city?.trim() && state && zipcode?.trim()
    );
  }, [formData]);

  const getRepContextFromState = useCallback(
    () => ({
      repsName: repName,
      repsBioguideID: repBioguideId,
      senOneName: senatorOneName,
      senOneBioguideID: senatorOneBioguideId,
      senTwoName: senatorTwoName,
      senTwoBioguideID: senatorTwoBioguideId,
    }),
    [
      repName,
      repBioguideId,
      senatorOneName,
      senatorOneBioguideId,
      senatorTwoName,
      senatorTwoBioguideId,
    ]
  );

  const handleContinue = async () => {
    if (changeMode && isMunicipalityAccount()) {
      const muniName = (formData.municipality_name || formData.entity_name || "").trim();
      if (!muniName) {
        setError("Please enter your municipality name.");
        return;
      }
    } else if (changeMode && isOrganizationAccount() && !formData.entity_name.trim()) {
      setError("Please enter your organization name.");
      return;
    }

    if (!allQuestionsAnswered) return;
    if (!changeMode && !onboardingUsernameReady) return;

    let claimedBioguideId = null;
    if (isCurrentRepresentative() && isThisYouAnswer === true && repBioguideId) {
      claimedBioguideId = repBioguideId;
    } else if (isCurrentSenator() && isThisYouAnswer === true && selectedSenatorBioguideId) {
      claimedBioguideId = selectedSenatorBioguideId;
    }

    try {
      setLoading(true);

      if (!changeMode) {
        await saveOnboardingUsername({ reloadContext: false });
      }

      if (changeMode && confirmMode) {
        await saveUserAddressFlags(formData, {
          repsName: contextRepsName,
          repsBioguideID: contextRepsBioguideID,
          senOneName: contextSenOneName,
          senOneBioguideID: contextSenOneBioguideID,
          senTwoName: contextSenTwoName,
          senTwoBioguideID: contextSenTwoBioguideID,
        });
      } else if (repName && repCalculated && (hasCompleteAddress() || isDistrictMode)) {
        // Reps are already resolved; this only persists the has-met answers.
        // In district mode no address fields are sent, so the backend leaves the
        // stored district and any existing address untouched.
        await saveUserAddressFlags(formData, getRepContextFromState());
        await loadUserContext();
      } else if (isDistrictMode) {
        await calculateSenatorsAndRepByDistrict(formData, claimedBioguideId);
        await loadUserContext();
      } else {
        await calculateSenatorsAndRep(formData, claimedBioguideId);
        await loadUserContext();
      }

      if (setAddressSkipped) setAddressSkipped(false);

      const profileUpdates = {
        is_state_visible: showLocationOnProfile,
        is_district_visible: showLocationOnProfile,
      };
      if (isOrganizationAccount() && formData.entity_name.trim()) {
        profileUpdates.entity_name = formData.entity_name.trim();
      }
      if (isMunicipalityAccount()) {
        const muniName = (formData.municipality_name || formData.entity_name || "").trim();
        if (muniName) {
          profileUpdates.entity_name = muniName;
        }
      }
      await updateFullProfile(profileUpdates);

      if (changeMode) {
        navigate("/username-selection", {
          state: buildUsernameNavigationState(true, claimedBioguideId),
        });
      } else {
        await loadUserContext();
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const shouldFallBackToDistrict = !isDistrictMode && isDistrictLookupUnavailable(err);
      if (shouldFallBackToDistrict) {
        setEntryMode(ENTRY_MODE.DISTRICT);
        returnToAddressForm();
      }

      const districtLookupMessage = getDistrictLookupErrorMessage(err, {
        allowSkip: !shouldFallBackToDistrict,
        allowDistrictFallback: shouldFallBackToDistrict,
      });
      let errorMessage = districtLookupMessage;
      if (!errorMessage) {
        if (err.code === "ECONNABORTED") {
          errorMessage = "Request timed out. Please check your internet connection and try again.";
        } else if (!err.response) {
          errorMessage = "Network error. Please check your internet connection and try again.";
        } else if (err.response?.status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (err.response?.status === 403) {
          errorMessage = "Access denied. Please ensure your email is verified and you are logged in, then try again.";
        } else if (err.response?.status === 400) {
          errorMessage =
            err.response?.data?.error ||
            err.response?.data?.detail ||
            "Invalid address. Please check your information and try again.";
        } else if (err.response?.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = "Failed to save your answers. Please try again.";
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipAddress = async () => {
    if (!onboardingUsernameReady || loading) return;

    try {
      setLoading(true);
      setError(null);
      await saveOnboardingUsername();
      if (setAddressSkipped) setAddressSkipped(true);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          "Failed to save your username. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const returnToAddressForm = useCallback(() => {
    setRepName(null);
    setSenatorOneName(null);
    setSenatorTwoName(null);
    setRepCalculated(false);
    setConfirmMode(false);
    setHasExitedConfirmMode(true);
    setIsThisYouAnswer(null);
    setSelectedSenatorBioguideId(null);
    setShowSenatorPicker(false);
    setRepBioguideId(null);
    setSenatorOneBioguideId(null);
    setSenatorTwoBioguideId(null);
    setRepPhotoUrl(null);
    setSenatorOnePhotoUrl(null);
    setSenatorTwoPhotoUrl(null);
    setFormData((prev) => ({
      ...prev,
      hasMetRep: undefined,
      hasMetSenator1: undefined,
      hasMetSenator2: undefined,
    }));
  }, []);

  const appliedSubviewRestoreRef = useRef(false);

  useEffect(() => {
    appliedSubviewRestoreRef.current = false;
  }, [location.key]);

  useEffect(() => {
    const showCalculatedReps = location.state?.showCalculatedReps;
    if (typeof showCalculatedReps !== "boolean") return;
    if (appliedSubviewRestoreRef.current) return;
    appliedSubviewRestoreRef.current = true;

    if (!showCalculatedReps) {
      returnToAddressForm();
      return;
    }

    const snap = location.state?.setupRestore || {};
    const resolvedRepName = snap.repName ?? contextRepsName;
    if (!resolvedRepName) {
      returnToAddressForm();
      return;
    }

    setRepName(resolvedRepName);
    setSenatorOneName(snap.senatorOneName ?? contextSenOneName ?? null);
    setSenatorTwoName(snap.senatorTwoName ?? contextSenTwoName ?? null);
    setRepBioguideId(snap.repBioguideId ?? contextRepsBioguideID ?? null);
    setSenatorOneBioguideId(snap.senatorOneBioguideId ?? contextSenOneBioguideID ?? null);
    setSenatorTwoBioguideId(snap.senatorTwoBioguideId ?? contextSenTwoBioguideID ?? null);
    setIsThisYouAnswer(
      snap.isThisYouAnswer !== undefined ? snap.isThisYouAnswer : null
    );
    setSelectedSenatorBioguideId(snap.selectedSenatorBioguideId ?? null);
    setRepCalculated(true);
    if (snap.confirmMode) {
      setConfirmMode(true);
    }
    setFormData((prev) => ({
      ...prev,
      street_1: snap.street_1 ?? prev.street_1,
      city: snap.city ?? prev.city,
      state: snap.state ?? prev.state,
      zipcode: snap.zipcode ?? prev.zipcode,
      entity_name: snap.entity_name ?? prev.entity_name,
      municipality_name: snap.municipality_name ?? prev.municipality_name,
      municipality_department:
        snap.municipality_department ?? prev.municipality_department,
      first_name: snap.first_name ?? prev.first_name,
      last_name: snap.last_name ?? prev.last_name,
      hasMetRep: snap.hasMetRep !== undefined ? snap.hasMetRep : prev.hasMetRep,
      hasMetSenator1:
        snap.hasMetSenator1 !== undefined ? snap.hasMetSenator1 : prev.hasMetSenator1,
      hasMetSenator2:
        snap.hasMetSenator2 !== undefined ? snap.hasMetSenator2 : prev.hasMetSenator2,
    }));
  }, [
    location.key,
    location.state,
    returnToAddressForm,
    contextRepsName,
    contextSenOneName,
    contextSenTwoName,
    contextRepsBioguideID,
    contextSenOneBioguideID,
    contextSenTwoBioguideID,
  ]);

  const handleRecalculate = () => {
    returnToAddressForm();
  };

  const handleEditAddress = () => {
    returnToAddressForm();
  };

  const isOnAddressEntryStep = !repName && !confirmMode;

  const getResumeStep = useCallback(() => {
    if (accountTypeName !== "public_official_candidate") return 1;
    if (publicOfficeStatus && publicOfficeLevel && publicOfficePosition) return 4;
    if (publicOfficeStatus && publicOfficeLevel) return 3;
    if (publicOfficeStatus) return 2;
    return 1;
  }, [accountTypeName, publicOfficeStatus, publicOfficeLevel, publicOfficePosition]);

  const navigateToAccountTypeSelection = useCallback(() => {
    if (!changeMode) return;
    const resumeStep = getResumeStep();
    navigate("/account-type-selection", {
      state: { changeMode: true, currentAccountType: accountTypeName, resumeStep },
    });
  }, [navigate, changeMode, getResumeStep, accountTypeName]);

  const handleGoBack = useCallback(() => {
    if (!isOnAddressEntryStep) {
      returnToAddressForm();
      return;
    }
    if (changeMode) {
      navigateToAccountTypeSelection();
    }
  }, [isOnAddressEntryStep, changeMode, returnToAddressForm, navigateToAccountTypeSelection]);

  const getHeaderBackLabel = () =>
    isOnAddressEntryStep ? "Account Type" : "Back";

  const showHeaderBackButton = !isOnAddressEntryStep || changeMode;

  const getTitle = () => {
    if (repName) {
      return confirmMode ? "Confirm Your Address" : "Your Representatives";
    }
    if (isMunicipalityAccount()) return "Let's Set Up Your Municipality";
    if (isOrganizationAccount()) return "Let's Set Up Your Organization";
    if (isPoliticianAccount(accountTypeName)) return "Let's Set Up Your Account";
    return "Let's Set You Up";
  };

  const getSubtitle = () => {
    if (!repName) return null;
    if (confirmMode) return "Please verify this is still your current address";
    // Municipality/org flows skip "have you met" questions — no input prompt copy.
    if (isMunicipalityAccount() || isOrganizationAccount()) return null;
    return "Your input helps us track which reps are actively connecting with their constituents.";
  };

  const getAddressDescription = () => {
    if (isDistrictMode) return "Tell us where you're represented so we can find your senators and representative.";
    if (isMunicipalityAccount()) return "Enter your city hall or municipal office address.";
    if (isOrganizationAccount()) return "Let's get your organization's address so we can find your senators and representative.";
    if (isPoliticianAccount(accountTypeName)) return "Enter your district office, campaign office, or government office address.";
    return "Let's get your address so we can find your senators and representative.";
  };

  const showNameFields = !isOrganizationAccount() && !isMunicipalityAccount() &&
    user?.providerData?.every((p) => p.providerId === "password");

  const renderQuestionButton = (field, value, label) => {
    const isSelected = formData[field] === value;
    return (
      <button
        key={`${field}-${String(value)}`}
        type="button"
        onClick={() => handleChange(field, value)}
        className={`flex-1 rounded-lg border-2 py-2.5 px-4 text-center text-sm font-semibold transition-all ${
          isSelected
            ? "border-[#047857] bg-[#047857] text-white"
            : "border-gray-300 bg-transparent text-gray-900 hover:border-gray-400"
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#047857] text-white">
        <div className={`mx-auto max-w-2xl px-4 flex items-center ${repName ? "py-3" : "py-4"}`}>
          {showHeaderBackButton && (
            <button
              type="button"
              onClick={handleGoBack}
              className="flex items-center gap-1 text-sm font-semibold text-white/90 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              {getHeaderBackLabel()}
            </button>
          )}
        </div>
      </div>

      <div className={`mx-auto max-w-2xl px-4 ${repName ? "py-5" : "py-8"}`}>
        <h1 className={`text-2xl font-bold text-gray-900 text-center ${repName ? "mb-1" : "mb-2"}`}>
          {changeMode && !changeModeAddressChecked ? "Confirm Your Address" : getTitle()}
        </h1>
        {getSubtitle() && !(changeMode && !changeModeAddressChecked) && (
          <p className={`text-base text-gray-700 text-center leading-snug ${repName ? "mb-3" : "mb-6 leading-relaxed"}`}>
            {getSubtitle()}
          </p>
        )}
        {changeMode && !changeModeAddressChecked && (
          <p className="text-base text-gray-700 text-center mb-6 leading-relaxed">
            Loading your address…
          </p>
        )}

        {changeMode && !changeModeAddressChecked ? (
          <div className="flex justify-center py-12" aria-label="Loading">
            <svg className="animate-spin h-10 w-10 text-[#047857]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : (
        <>
        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-left">
            <svg className="h-5 w-5 text-red-500 shrink-0 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-900 m-0 leading-snug">{error}</p>
          </div>
        )}

        {showOnboardingUsername && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-800 mb-1.5" htmlFor="onboarding-username">
              Username
            </label>
            <div className="relative">
              <span
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 select-none text-lg font-semibold text-[#047857]"
                aria-hidden
              >
                @
              </span>
              <input
                id="onboarding-username"
                type="text"
                value={onboardingUsername}
                onChange={(e) => {
                  const raw = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
                  currentUsernameRef.current = raw;
                  usernameManuallyEditedRef.current = true;
                  setOnboardingUsername(raw);
                  if (raw && raw === validatedUsernameRef.current) {
                    setUsernameValidationState("available");
                    setUsernameValidationMessage("This username is available!");
                  } else {
                    setUsernameValidationState("idle");
                    setUsernameValidationMessage("");
                  }
                }}
                placeholder="your_username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={30}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-9 pr-4 text-base text-gray-900 placeholder:text-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857]"
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {usernameValidationMessage || "You can change this later in Account management."}
            </p>
          </div>
        )}

        {!repName ? (
          <>
            {/* Name fields */}
            {showNameFields && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    placeholder="Your first name"
                    disabled={repCalculated}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    placeholder="Your last name"
                    disabled={repCalculated}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            )}

            {/* Municipality fields */}
            {isMunicipalityAccount() && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Municipality Name</label>
                  <input
                    type="text"
                    value={formData.municipality_name}
                    onChange={(e) => handleChange("municipality_name", e.target.value)}
                    placeholder="e.g., City of Santa Cruz"
                    disabled={repCalculated}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Department / Agency</label>
                  <input
                    type="text"
                    value={formData.municipality_department}
                    onChange={(e) => handleChange("municipality_department", e.target.value)}
                    placeholder="e.g., Parks & Recreation"
                    disabled={repCalculated}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            )}

            {/* Organization name field */}
            {isOrganizationAccount() && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">Organization Name</label>
                <input
                  type="text"
                  value={formData.entity_name}
                  onChange={(e) => handleChange("entity_name", e.target.value)}
                  placeholder="Your organization's name"
                  disabled={repCalculated}
                  maxLength={500}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            )}

            {!isDistrictMode && (
              <p className="text-sm text-gray-900 mb-5 leading-relaxed text-center">{getAddressDescription()}</p>
            )}

            {/* Mode toggle: Search Address / Enter Manually / I Know My District */}
            <div className={`flex rounded-xl border border-gray-200 p-1 bg-gray-50 ${isDistrictMode ? "mb-2" : "mb-5"}`} role="tablist">
              {hasApiKey && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isSearchMode}
                  onClick={() => setEntryMode(ENTRY_MODE.SEARCH)}
                  disabled={repCalculated}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-2 text-sm font-semibold transition-all ${
                    isSearchMode
                      ? "bg-[#047857] text-white"
                      : "bg-transparent text-gray-700 hover:bg-gray-100"
                  } ${repCalculated ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <svg className="hidden sm:block h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </button>
              )}
              <button
                type="button"
                role="tab"
                aria-selected={isManualMode}
                onClick={() => setEntryMode(ENTRY_MODE.MANUAL)}
                disabled={repCalculated}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-2 text-sm font-semibold transition-all ${
                  isManualMode
                    ? "bg-[#047857] text-white"
                    : "bg-transparent text-gray-700 hover:bg-gray-100"
                } ${repCalculated ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <svg className="hidden sm:block h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Enter Address
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isDistrictMode}
                onClick={() => setEntryMode(ENTRY_MODE.DISTRICT)}
                disabled={repCalculated}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-2 text-sm font-semibold transition-all ${
                  isDistrictMode
                    ? "bg-[#047857] text-white"
                    : "bg-transparent text-gray-700 hover:bg-gray-100"
                } ${repCalculated ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <svg className="hidden sm:block h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                My District
              </button>
            </div>

            {/* District mode: state + district, no address and no Census lookup */}
            {isDistrictMode && (
              <div className="space-y-4 mb-6">
                <p className="text-sm text-gray-700 leading-snug">
                  Already know your congressional district? Pick your state and district and we'll
                  find your representatives directly.
                </p>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">State</label>
                  <button
                    type="button"
                    onClick={() => !repCalculated && setShowStatePicker(true)}
                    disabled={repCalculated}
                    className="w-full flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-base text-left disabled:bg-gray-50 disabled:text-gray-500 hover:border-gray-300 transition-colors"
                  >
                    <span className={formData.state ? "text-gray-900" : "text-gray-400"}>
                      {formData.state
                        ? stateOptions.find((s) => s.value === formData.state)?.label || formData.state
                        : "Select State"}
                    </span>
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                <div>
                  <label htmlFor="district-select" className="block text-sm font-semibold text-gray-800 mb-1.5">
                    Congressional District
                  </label>
                  <select
                    id="district-select"
                    value={formData.district_number}
                    onChange={(e) => handleChange("district_number", e.target.value)}
                    disabled={repCalculated || !formData.state || districtChoices.length <= 1}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    {!formData.state && <option value="">Select a state first</option>}
                    {formData.state && districtChoices.length > 1 && (
                      <option value="">Select District</option>
                    )}
                    {districtChoices.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                  {formData.state && districtChoices.length === 1 && (
                    <p className="mt-1.5 text-sm text-gray-700">
                      {formData.state} has a single at-large seat, so there's nothing to choose here.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Search Address mode: Google Places autocomplete */}
            {isSearchMode && !repCalculated && (
              <div className="space-y-4 mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">Search for your address</label>
                <GooglePlacesAutocomplete
                  apiKey={GOOGLE_PLACES_API_KEY}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: "us",
                    },
                  }}
                  debounce={300}
                  selectProps={{
                    value: selectedPlace,
                    onChange: handlePlaceSelection,
                    placeholder: "Start typing your address...",
                    isClearable: true,
                    onInputChange: () => {
                      if (error) setError(null);
                    },
                    styles: {
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: 50,
                        borderRadius: 12,
                        borderColor: state.isFocused ? "#047857" : "#e5e7eb",
                        boxShadow: state.isFocused ? "0 0 0 1px #047857" : "none",
                        "&:hover": { borderColor: state.isFocused ? "#047857" : "#d1d5db" },
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#9ca3af",
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 60,
                        borderRadius: 12,
                        overflow: "hidden",
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? "#f8fafc" : "#fff",
                        color: "#111827",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }),
                    },
                  }}
                />
                {(formData.street_1 || formData.city || formData.state || formData.zipcode) && (
                  <div className="rounded-xl border border-[#047857]/20 bg-[#047857]/5 p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Selected Address:</p>
                    <p className="text-base text-gray-800">{formData.street_1}</p>
                    <p className="text-base text-gray-800">
                      {[formData.city, formData.state, formData.zipcode].filter(Boolean).join(", ")}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          street_1: "",
                          city: "",
                          state: "",
                          zipcode: "",
                        }));
                        setSelectedPlace(null);
                      }}
                      className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-[#047857] hover:text-[#065f46]"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Clear
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Manual entry: address fields */}
            {isManualMode && (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">Street Address</label>
                  <input
                    type="text"
                    value={formData.street_1}
                    onChange={(e) => handleChange("street_1", e.target.value)}
                    placeholder="123 Main St"
                    disabled={repCalculated}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="City"
                    disabled={repCalculated}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">State</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => !repCalculated && setShowStatePicker(true)}
                        disabled={repCalculated}
                        className="w-full flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-base text-left disabled:bg-gray-50 disabled:text-gray-500 hover:border-gray-300 transition-colors"
                      >
                        <span className={formData.state ? "text-gray-900" : "text-gray-400"}>
                          {formData.state
                            ? stateOptions.find((s) => s.value === formData.state)?.label || formData.state
                            : "Select State"}
                        </span>
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">Zipcode</label>
                    <input
                      type="text"
                      value={formData.zipcode}
                      onChange={(e) => handleChange("zipcode", e.target.value)}
                      placeholder="12345"
                      maxLength={5}
                      disabled={repCalculated}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857] disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Calculate button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`w-full rounded-xl py-3.5 text-base font-bold text-white transition-all ${
                loading ? "bg-[#047857]/60 cursor-not-allowed" : "bg-[#047857] hover:bg-[#065f46]"
              }`}
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Calculating…
                </span>
              ) : (
                "Find My Representatives"
              )}
            </button>

            {/* Skip address */}
            {!changeMode && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleSkipAddress}
                  disabled={!onboardingUsernameReady || loading}
                  className={`w-full rounded-xl py-3.5 text-base font-bold transition-all ${
                    !onboardingUsernameReady || loading
                      ? "bg-gray-300 text-white cursor-not-allowed"
                      : "bg-white border-2 border-[#047857] text-[#047857] hover:bg-emerald-50"
                  }`}
                >
                  {loading ? "Saving…" : "Skip for now"}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Confirm mode address card */}
            {confirmMode && (
              <div className="mb-6 rounded-xl border border-[#047857]/30 bg-[#047857]/5 py-3 px-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <svg className="h-6 w-6 text-[#047857]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-base font-bold text-gray-900">Your Current Address</span>
                </div>
                <div className="mb-2">
                  {(formData.entity_name || formData.municipality_name || `${formData.first_name} ${formData.last_name}`.trim()) && (
                    <p className="text-base font-semibold text-gray-900">
                      {formData.entity_name || formData.municipality_name || `${formData.first_name} ${formData.last_name}`.trim()}
                    </p>
                  )}
                  <p className="text-base text-gray-900">{formData.street_1}</p>
                  <p className="text-base text-gray-900">
                    {formData.city}, {formData.state} {formData.zipcode}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleEditAddress}
                  className="flex items-center justify-center gap-1.5 w-full rounded-lg border border-[#047857] bg-white py-2.5 text-sm font-semibold text-[#047857] hover:bg-[#047857]/5 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Edit Address
                </button>
              </div>
            )}

            {/* Entity name in confirm mode */}
            {confirmMode && (isOrganizationAccount() || isMunicipalityAccount()) && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  {isMunicipalityAccount() ? "Municipality Name" : "Organization Name"}
                </label>
                <input
                  type="text"
                  value={formData.entity_name}
                  onChange={(e) => handleChange("entity_name", e.target.value)}
                  placeholder={isMunicipalityAccount() ? "e.g., City of Santa Cruz" : "Your organization's name"}
                  maxLength={500}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 outline-none focus:border-[#047857] focus:ring-1 focus:ring-[#047857]"
                />
              </div>
            )}

            {/* Rep results */}
            <div className="space-y-2 mb-3">
              <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                <p className="text-sm font-medium text-gray-900 text-center mb-1.5">{isDCResident() ? "Delegate" : "Representative"}</p>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#047857]/10 ring-2 ring-[#047857]/20">
                    {repPhotoUrl ? (
                      <img src={repPhotoUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <svg className="h-5 w-5 text-[#047857]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <p className="text-base font-semibold text-gray-900">{repName}</p>
                </div>
              </div>

              {!isDCResident() && (
                <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                  <p className="text-sm font-medium text-gray-900 text-center mb-1.5">Senators</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#047857]/10 ring-2 ring-[#047857]/20">
                        {senatorOnePhotoUrl ? (
                          <img src={senatorOnePhotoUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <svg className="h-5 w-5 text-[#047857]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                      </div>
                      <p className="text-base font-semibold text-gray-900">{senatorOneName}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#047857]/10 ring-2 ring-[#047857]/20">
                        {senatorTwoPhotoUrl ? (
                          <img src={senatorTwoPhotoUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <svg className="h-5 w-5 text-[#047857]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                      </div>
                      <p className="text-base font-semibold text-gray-900">{senatorTwoName}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* DC info */}
            {isDCResident() && (
              <div className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 p-3 mb-4">
                <svg className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-900">
                  Washington D.C. has one non-voting Delegate to the House and no U.S. Senators.
                </p>
              </div>
            )}

            {/* "Is this you?" for current rep */}
            {isCurrentRepresentative() && repBioguideId && (
              <div className="mb-4">
                <div className="flex items-center gap-3 rounded-xl border border-[#047857]/30 bg-[#047857]/5 p-3 mb-2">
                  <svg className="h-7 w-7 text-[#047857] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-[#047857]">Is this you?</p>
                    <p className="text-lg font-bold text-gray-900">{repName}</p>
                    <p className="text-sm font-medium text-gray-900">U.S. Representative</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsThisYouAnswer(true)}
                    className={`flex-1 rounded-lg border-2 py-3 text-sm font-semibold transition-all ${
                      isThisYouAnswer === true
                        ? "border-[#047857] bg-[#047857] text-white"
                        : "border-gray-300 text-gray-900 hover:border-gray-400"
                    }`}
                  >
                    Yes, that's me
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsThisYouAnswer(false)}
                    className={`flex-1 rounded-lg border-2 py-3 text-sm font-semibold transition-all ${
                      isThisYouAnswer === false
                        ? "border-[#047857] bg-[#047857] text-white"
                        : "border-gray-300 text-gray-900 hover:border-gray-400"
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            )}

            {/* "Are you one of these senators?" for current senator */}
            {isCurrentSenator() && (senatorOneBioguideId || senatorTwoBioguideId) && !isDCResident() && (
              <div className="mb-4">
                <div className="flex items-center gap-3 rounded-xl border border-[#047857]/30 bg-[#047857]/5 p-3 mb-2">
                  <svg className="h-7 w-7 text-[#047857] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-[#047857]">Are you one of these senators?</p>
                    <p className="text-lg font-bold text-gray-900">{senatorOneName}</p>
                    <p className="text-lg font-bold text-gray-900">{senatorTwoName}</p>
                    <p className="text-sm font-medium text-gray-900">U.S. Senators</p>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => { setIsThisYouAnswer(true); setShowSenatorPicker(true); }}
                    className={`flex-1 rounded-lg border-2 py-3 text-sm font-semibold transition-all ${
                      isThisYouAnswer === true
                        ? "border-[#047857] bg-[#047857] text-white"
                        : "border-gray-300 text-gray-900 hover:border-gray-400"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIsThisYouAnswer(false); setShowSenatorPicker(false); setSelectedSenatorBioguideId(null); }}
                    className={`flex-1 rounded-lg border-2 py-3 text-sm font-semibold transition-all ${
                      isThisYouAnswer === false
                        ? "border-[#047857] bg-[#047857] text-white"
                        : "border-gray-300 text-gray-900 hover:border-gray-400"
                    }`}
                  >
                    No
                  </button>
                </div>

                {showSenatorPicker && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Which senator are you?</p>
                    {senatorOneBioguideId && (
                      <button
                        type="button"
                        onClick={() => setSelectedSenatorBioguideId(senatorOneBioguideId)}
                        className={`w-full flex items-center gap-3 rounded-lg border-2 p-3.5 text-left transition-all ${
                          selectedSenatorBioguideId === senatorOneBioguideId
                            ? "border-[#047857] bg-[#047857]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                          selectedSenatorBioguideId === senatorOneBioguideId ? "border-[#047857]" : "border-gray-300"
                        }`}>
                          {selectedSenatorBioguideId === senatorOneBioguideId && (
                            <div className="h-2.5 w-2.5 rounded-full bg-[#047857]" />
                          )}
                        </div>
                        <span className={`text-sm font-semibold ${
                          selectedSenatorBioguideId === senatorOneBioguideId ? "text-[#047857]" : "text-gray-900"
                        }`}>
                          {senatorOneName}
                        </span>
                      </button>
                    )}
                    {senatorTwoBioguideId && (
                      <button
                        type="button"
                        onClick={() => setSelectedSenatorBioguideId(senatorTwoBioguideId)}
                        className={`w-full flex items-center gap-3 rounded-lg border-2 p-3.5 text-left transition-all ${
                          selectedSenatorBioguideId === senatorTwoBioguideId
                            ? "border-[#047857] bg-[#047857]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                          selectedSenatorBioguideId === senatorTwoBioguideId ? "border-[#047857]" : "border-gray-300"
                        }`}>
                          {selectedSenatorBioguideId === senatorTwoBioguideId && (
                            <div className="h-2.5 w-2.5 rounded-full bg-[#047857]" />
                          )}
                        </div>
                        <span className={`text-sm font-semibold ${
                          selectedSenatorBioguideId === senatorTwoBioguideId ? "text-[#047857]" : "text-gray-900"
                        }`}>
                          {senatorTwoName}
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Recalculate */}
            <div className="flex justify-center mb-2">
              <button
                type="button"
                onClick={handleRecalculate}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#047857] hover:text-[#065f46] transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Recalculate
              </button>
            </div>

            {/* Questions */}
            {!isOrganizationAccount() && !isMunicipalityAccount() && (
              <div className="mb-4">
                {!allQuestionsAnswered && (
                  <p className="text-sm font-semibold text-red-600 text-center mb-2 italic">
                    * To continue, answer all questions to the best of your ability.
                  </p>
                )}
                <div className="space-y-3">
                <div>
                  <p className="text-base font-semibold text-gray-900 mb-2">
                    {isCurrentRepresentative()
                      ? `Have you met ${repName}? If this is you, select N/A.`
                      : `Have you met ${repName}?`}
                  </p>
                  <div className="flex gap-3">
                    {renderQuestionButton("hasMetRep", true, "Yes")}
                    {renderQuestionButton("hasMetRep", false, "No")}
                    {isCurrentRepresentative() && renderQuestionButton("hasMetRep", null, "N/A")}
                  </div>
                </div>

                {!isDCResident() && (
                  <>
                    <div>
                      <p className="text-base font-semibold text-gray-900 mb-2">
                        {isCurrentSenator()
                          ? `Have you met ${senatorOneName}? If this is you, select N/A.`
                          : `Have you met ${senatorOneName}?`}
                      </p>
                      <div className="flex gap-3">
                        {renderQuestionButton("hasMetSenator1", true, "Yes")}
                        {renderQuestionButton("hasMetSenator1", false, "No")}
                        {isCurrentSenator() && renderQuestionButton("hasMetSenator1", null, "N/A")}
                      </div>
                    </div>

                    <div>
                      <p className="text-base font-semibold text-gray-900 mb-2">
                        {isCurrentSenator()
                          ? `Have you met ${senatorTwoName}? If this is you, select N/A.`
                          : `Have you met ${senatorTwoName}?`}
                      </p>
                      <div className="flex gap-3">
                        {renderQuestionButton("hasMetSenator2", true, "Yes")}
                        {renderQuestionButton("hasMetSenator2", false, "No")}
                        {isCurrentSenator() && renderQuestionButton("hasMetSenator2", null, "N/A")}
                      </div>
                    </div>
                  </>
                )}
                </div>
              </div>
            )}

            {!onboardingUsernameReady && (
              <p className="text-sm font-semibold text-red-600 text-center mb-2 italic">
                * To continue, choose an available username above.
              </p>
            )}

            {/* Location privacy toggle */}
            <button
              type="button"
              onClick={() => setShowLocationOnProfile(!showLocationOnProfile)}
              className="flex items-center gap-3 w-full py-2 px-1 mb-3"
            >
              <div className={`h-6 w-6 rounded-md flex items-center justify-center border-2 transition-colors ${
                showLocationOnProfile ? "border-[#047857] bg-[#047857]" : "border-gray-300"
              }`}>
                {showLocationOnProfile && (
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-gray-900 text-left">
                Show my state and district on my public profile
              </span>
            </button>

            {/* Continue button */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={!allQuestionsAnswered || loading || (!changeMode && !onboardingUsernameReady)}
              className={`w-full rounded-xl py-3.5 text-base font-bold text-white transition-all ${
                !allQuestionsAnswered || loading || (!changeMode && !onboardingUsernameReady)
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-[#047857] hover:bg-[#065f46]"
              }`}
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Saving…
                </span>
              ) : (
                confirmMode ? "Confirm & Continue" : "Next"
              )}
            </button>
          </>
        )}
        </>
        )}
      </div>

      {/* State picker modal */}
      {showStatePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowStatePicker(false)}>
          <div
            className="w-full max-w-lg bg-white rounded-2xl max-h-[40vh] min-h-[160px] flex flex-col shadow-xl -translate-y-[8vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Select State</h3>
              <button type="button" onClick={() => setShowStatePicker(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {stateOptions.map((state) => (
                <button
                  key={state.value}
                  type="button"
                  onClick={() => {
                    setFormData((prev) =>
                      prev.state === state.value
                        ? prev
                        : { ...prev, state: state.value, district_number: "" }
                    );
                    setShowStatePicker(false);
                  }}
                  className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors ${
                    formData.state === state.value ? "bg-[#047857]/5" : ""
                  }`}
                >
                  <span className={`text-base ${formData.state === state.value ? "font-semibold text-[#047857]" : "text-gray-900"}`}>
                    {state.label}
                  </span>
                  {formData.state === state.value && (
                    <svg className="h-5 w-5 text-[#047857]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
